import { Connection } from 'features/connections'
import {
  METADATA_KEY,
  useBookmarks as useBookmarks,
  useCreateBookmark,
  useDeleteBookmark
} from '../api/bookmarksQueries'
import { useCallback, useMemo } from 'react'
import { queryClient } from 'queryClient'
import { DataExplorerResource, DataTreeResourceRequestProps } from 'features/new-data-explorer/types'
import { InfiniteData, QueryKey, useQueryClient } from 'react-query'
import { useDataExplorerContext } from 'features/new-data-explorer/ui/DataExplorerContext/DataExplorerContext'
import useDataExplorerQueries, { DATA_EXPLORER_KEYS } from 'features/new-data-explorer/api/dataExplorerQueries'
import { connectionsQueryKeys } from 'features/connections/api/connectionsQueries'

const includeSelectedResourceInBookmarksTree = (
  tree: Record<string, unknown> = {},
  resourcesParams: DataTreeResourceRequestProps
) => {
  const fqns = resourcesParams.fqn?.split('.').map((_, index, arr) => arr.slice(0, index + 1).join('.')) || []
  const pathToInclude = [resourcesParams.connectionId, ...fqns]
  let currentTree = tree

  pathToInclude.forEach((path) => {
    if (!path) return

    const currentParentData = queryClient.getQueryData<InfiniteData<DataExplorerResource>>([
      'resources',
      resourcesParams.connectionId,
      path.split('.').slice(0, -1).join('.') || null,
      null
    ]) as InfiniteData<DataExplorerResource>
    const currentItem = currentParentData?.pages?.[0].children?.find((c) => c.id === path)
    if (!currentTree[path]) {
      currentTree[path] = {
        [METADATA_KEY]: {
          name: currentItem?.name || path,
          type: currentItem?.type || 'project'
        }
      }
    }
    currentTree = currentTree[path] as Record<string, unknown>
  })
  return tree
}

export function useBookmarksActions() {
  const queryClient = useQueryClient()
  const { queries } = useDataExplorerQueries()
  const { data: bookmarksTree } = useBookmarks()
  const { showBookmarks, setShowBookmarks, expandNode, resourcesParams, selectedConnection } = useDataExplorerContext()

  const updateDataExplorerWithBookmarksBackup = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tree?: Record<string, any>, connectionId?: string) => {
      if (!tree) return
      Object.entries(tree)
        .filter(([key]) => key !== METADATA_KEY)
        .forEach(([key, nextTree]) => {
          if (!nextTree[METADATA_KEY]?.isLeaf) {
            updateDataExplorerWithBookmarksBackup(nextTree, connectionId || key)
            const queryKey = queries.getResources({
              connectionId: connectionId || key,
              fqn: connectionId ? key : undefined
            }).queryKey as QueryKey

            const backupData = queryClient.getQueryData([...DATA_EXPLORER_KEYS.backup, ...queryKey])
            queryClient.setQueryData(queryKey, backupData)
          }
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateDataExplorerWithBookmarks = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (tree?: Record<string, any>, connectionId?: string) => {
      if (!tree) return
      Object.entries(tree)
        .filter(([key]) => key !== METADATA_KEY)
        .forEach(([key, nextTree]) => {
          if (!nextTree[METADATA_KEY]?.isLeaf) {
            updateDataExplorerWithBookmarks(nextTree, connectionId || key)
            const children = Object.entries(nextTree).filter(([k]) => k !== METADATA_KEY)
            const queryKey = queries.getResources({
              connectionId: connectionId || key,
              fqn: connectionId ? key : undefined
            }).queryKey as QueryKey

            // DO backup instead of invalidate
            const backupData = queryClient.getQueryData(queryKey)
            queryClient.setQueryData([...DATA_EXPLORER_KEYS.backup, ...queryKey], backupData)

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            queryClient.setQueryData(queryKey, (oldData: any) => {
              return {
                pages: [
                  {
                    ...(oldData?.pages?.[0] || []),
                    totalChildren: children.length,
                    type: tree?.[key]?.[METADATA_KEY]?.type || 'project',
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    children: children.map(([k, v]: [string, any]) => {
                      return {
                        id: k,
                        name: v[METADATA_KEY].name || k,
                        type: v[METADATA_KEY].type || 'project',
                        location: v[METADATA_KEY].location || undefined
                      }
                    })
                  }
                ]
              }
            })
          }
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient]
  )

  const toggleBookmarks = useCallback(() => {
    const newShowBookmarks = !showBookmarks
    setShowBookmarks(newShowBookmarks)
    if (newShowBookmarks) {
      let tree = bookmarksTree
      const shouldIncludeSelectedResource = !selectedConnection?.starred
      if (shouldIncludeSelectedResource) {
        tree = includeSelectedResourceInBookmarksTree(bookmarksTree, resourcesParams)
      }
      queryClient.setQueryData([...DATA_EXPLORER_KEYS.backupTree], tree)
      updateDataExplorerWithBookmarks(tree)
    } else {
      // Should close all expanded nodes expect the one that is selected. Invalidate all queries and refetch the one that is selected
      const backupTree = queryClient.getQueryData<Record<string, unknown>>([...DATA_EXPLORER_KEYS.backupTree])
      const expandedNodes = calculeNecessaryExpandedNodes(resourcesParams)
      expandedNodes.forEach((node) => {
        expandNode(node)
      })
      updateDataExplorerWithBookmarksBackup(backupTree)
    }
  }, [
    selectedConnection,
    bookmarksTree,
    updateDataExplorerWithBookmarks,
    updateDataExplorerWithBookmarksBackup,
    showBookmarks,
    expandNode,
    resourcesParams,
    setShowBookmarks,
    queryClient
  ])

  return { toggleBookmarks }
}

export function useBookmarkItem({ connection, fqn }: { connection: Connection; fqn?: string }) {
  const { raw: bookmarksRaw } = useBookmarks()
  const { mutateAsync: createBookmark } = useCreateBookmark()
  const { mutateAsync: deleteBookmark } = useDeleteBookmark()
  const normalizedFqn = useMemo(() => fqn?.replace(/`/g, ''), [fqn])
  const starred = useMemo(() => {
    if (!normalizedFqn) {
      return connection?.starred
    } else {
      return bookmarksRaw?.[connection?.id]?.some((b) => b.fqn.replace(/`/g, '') === normalizedFqn)
    }
  }, [bookmarksRaw, connection?.id, connection?.starred, normalizedFqn])

  const getMetadata = (fqn: string) => {
    const metadata = [undefined, ...fqn.split('.')]
      .map((_, idx, original) => original.slice(1, idx + 1).join('.'))
      .map((id, idx, original) => {
        if (original.length === idx + 1) {
          return
        }

        const nextId = original[idx + 1]
        const data = queryClient.getQueryData<InfiniteData<DataExplorerResource>>([
          'resources',
          connection.id,
          id || null,
          null
        ]) as InfiniteData<DataExplorerResource>
        const item = data.pages
          .flatMap((page) => page.children || [])
          .find((c) => c.id === nextId) as DataExplorerResource
        return item
      })
      .filter(Boolean) as DataExplorerResource[]

    return metadata.reduce(
      (acc, item: DataExplorerResource) => {
        acc.name.push(item.name as string)
        acc.type.push(item.type as string)
        acc.location.push(item.location || '')
        return acc
      },
      {
        name: [] as string[],
        type: [] as string[],
        location: [] as string[]
      }
    )
  }

  const updateConnectionStarredStatus = (connectionId: string, starred: boolean) => {
    queryClient.setQueryData(connectionsQueryKeys.connections, (oldData: Connection[] | undefined) => {
      if (!oldData) return []
      return oldData.map((c) => {
        if (connectionId === c.id) {
          return { ...c, starred }
        }
        return c
      })
    })
  }

  const handleBookmarkClick = async () => {
    try {
      if (starred) {
        await deleteBookmark({
          connectionId: connection.id,
          bookmark: normalizedFqn ? { fqn: normalizedFqn } : undefined
        })
      } else {
        const metadata = normalizedFqn ? getMetadata(normalizedFqn) : undefined
        await createBookmark({
          connectionId: connection.id,
          bookmark: normalizedFqn ? { fqn: normalizedFqn, metadata } : undefined
        })
      }

      if (!normalizedFqn) {
        updateConnectionStarredStatus(connection.id, !starred)
      } else {
        queryClient.invalidateQueries(['bookmarks'])
      }
    } catch (error) {
      console.error('Failed to update bookmark:', error)
    }
  }
  return { starred, handleBookmarkClick }
}

function calculeNecessaryExpandedNodes(resourcesParams: DataTreeResourceRequestProps) {
  if (!resourcesParams.connectionId) return []

  const necessaryExpandedNodes = [resourcesParams.connectionId]
  const selectedIds = resourcesParams.fqn?.split('.')
  selectedIds?.forEach((id, idx, original) => {
    necessaryExpandedNodes.push(`${resourcesParams.connectionId} > ${original.slice(0, idx + 1).join('.')}`)
  })
  return necessaryExpandedNodes
}
